/**
 * Geradores de JSON-LD para schema.org.
 * Usado por SEO component em <head> via React 19 (document.title + meta tags injetadas).
 */

import { siteConfig } from "@/site.config";

/** Remove chaves com valor vazio/undefined para não vazar placeholders no JSON-LD. */
const omitEmpty = <T extends Record<string, unknown>>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== "" && v != null)
  ) as Partial<T>;

/** PostalAddress só se houver logradouro real (evita endereço fake no structured-data). */
const postalAddress = () => {
  const a = siteConfig.brand.address;
  if (!a.street) return undefined;
  return omitEmpty({
    "@type": "PostalAddress",
    streetAddress: a.street,
    addressLocality: a.city,
    addressRegion: a.state,
    postalCode: a.zip,
    addressCountry: a.country,
  });
};

export const buildOrganizationLD = () =>
  omitEmpty({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteConfig.brand.name,
    legalName: siteConfig.brand.legalName,
    url: siteConfig.seo.canonicalBase,
    logo: `${siteConfig.seo.canonicalBase}/logo.png`,
    email: siteConfig.brand.email,
    telephone: siteConfig.brand.phoneLandline,
    taxID: siteConfig.brand.cnpj,
    address: postalAddress(),
    sameAs: Object.values(siteConfig.brand.social).filter(Boolean),
  });

export const buildLocalBusinessLD = () =>
  omitEmpty({
  "@context": "https://schema.org",
  "@type": "FinancialService",
  "@id": `${siteConfig.seo.canonicalBase}#localbusiness`,
  name: siteConfig.brand.name,
  description: siteConfig.seo.defaultDescription,
  url: siteConfig.seo.canonicalBase,
  telephone: siteConfig.brand.phoneLandline,
  email: siteConfig.brand.email,
  image: `${siteConfig.seo.canonicalBase}${siteConfig.seo.ogImage}`,
  priceRange: "R$",
  areaServed: { "@type": "Country", name: "Brasil" },
  address: postalAddress(),
  geo: siteConfig.brand.address.geo
    ? {
        "@type": "GeoCoordinates",
        latitude: siteConfig.brand.address.geo.lat,
        longitude: siteConfig.brand.address.geo.lng,
      }
    : undefined,
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday"],
      opens: "08:00",
      closes: "12:00",
    },
  ],
});

export type FaqEntry = { question: string; answer: string };

export const buildFaqPageLD = (faqs: FaqEntry[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(f => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});

export const buildServiceLD = (params: {
  serviceType: string;
  name: string;
  description: string;
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: params.serviceType,
  name: params.name,
  description: params.description,
  url: params.url,
  provider: { "@id": `${siteConfig.seo.canonicalBase}#localbusiness` },
  areaServed: [
    { "@type": "AdministrativeArea", name: "Minas Gerais" },
    { "@type": "Country", name: "Brasil" },
  ],
});

/** AggregateRating Schema, uso na Hero (selo "Nota 4.8") */
export const buildAggregateRatingLD = (params: {
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
}) => ({
  "@context": "https://schema.org",
  "@type": "AggregateRating",
  itemReviewed: { "@id": `${siteConfig.seo.canonicalBase}#localbusiness` },
  ratingValue: params.ratingValue,
  reviewCount: params.reviewCount,
  bestRating: params.bestRating ?? 5,
});

/** Review Schema (agregado dos Testimonials) */
export const buildReviewLD = (params: {
  author: string;
  reviewBody: string;
  ratingValue?: number;
  datePublished?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Review",
  itemReviewed: { "@id": `${siteConfig.seo.canonicalBase}#localbusiness` },
  author: { "@type": "Person", name: params.author },
  reviewBody: params.reviewBody,
  reviewRating: {
    "@type": "Rating",
    ratingValue: params.ratingValue ?? 5,
    bestRating: 5,
  },
  datePublished: params.datePublished,
});

/** Offer Schema, para cards Vehicles / Properties */
export const buildOfferLD = (params: {
  name: string;
  description?: string;
  image?: string;
  url?: string;
  price: number;
  priceCurrency?: string;
  category?: string;
  availability?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: params.name,
  description: params.description,
  image: params.image,
  url: params.url,
  category: params.category,
  offers: {
    "@type": "Offer",
    price: params.price,
    priceCurrency: params.priceCurrency ?? "BRL",
    availability: params.availability ?? "https://schema.org/InStock",
    seller: { "@id": `${siteConfig.seo.canonicalBase}#localbusiness` },
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: params.price,
      priceCurrency: params.priceCurrency ?? "BRL",
      unitText: "mês",
    },
  },
});

/** BreadcrumbList Schema, para futuras ALPs verticais */
export const buildBreadcrumbLD = (
  items: Array<{ name: string; url: string }>
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

/** WebSite Schema, para SiteLinks Search Box no Google */
export const buildWebSiteLD = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: siteConfig.brand.name,
  url: siteConfig.seo.canonicalBase,
  potentialAction: {
    "@type": "SearchAction",
    target: `${siteConfig.seo.canonicalBase}/?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});
